import mproLabOverview from './content/mpro-lab-overview.md?raw'
import licences from './content/licences.md?raw'
import mproToken from './content/mpro-token.md?raw'
import dao from './content/dao.md?raw'
import community from './content/community.md?raw'
import marketplace from './content/marketplace.md?raw'
import feeVault from './content/fee-vault.md?raw'
import swap from './content/swap.md?raw'
import tokenizedStocks from './content/tokenized-stocks.md?raw'
import storage from './content/storage.md?raw'
import terminal from './content/terminal.md?raw'
import terminalLicence from './content/terminal-licence.md?raw'
import terminalWallet from './content/terminal-wallet.md?raw'
import terminalAssistant from './content/terminal-assistant.md?raw'
import terminalModels from './content/terminal-models.md?raw'
import terminalSkills from './content/terminal-skills.md?raw'
import terminalChannels from './content/terminal-channels.md?raw'
import terminalMarketplace from './content/terminal-marketplace.md?raw'
import gettingHelp from './content/getting-help.md?raw'

export type DocumentationItem = {
  slug: string
  title: string
  /**
   * One line saying what this page settles — what a reader gets by opening it,
   * and especially any fee or rule it is the only page to state.
   *
   * It exists because a title is not enough to choose by. The index this feeds
   * carried titles alone, and an agent writing the platform's Terms of Use read
   * every page whose title announced a fee, concluded it had them all, and
   * published that swapping is free — while `swap.md`, titled "Trading tokens
   * on Base", had described the router and its 0.25% in its second paragraph
   * since the day before. Nothing was missing from the documentation; the way
   * to find it was.
   */
  summary: string
  markdown: string
}

export type DocumentationGroup = {
  id: string
  title: string
  items: DocumentationItem[]
}

export const DOCUMENTATION_DEFAULT_SLUG = 'mpro-lab-overview'

export const documentationRegistry: DocumentationGroup[] = [
  {
    id: 'mpro-lab',
    title: 'MPRO Lab',
    items: [
      {
        slug: 'mpro-lab-overview',
        title: 'Overview',
        summary:
          'What the platform is, what runs on chain, and who maintains it.',
        markdown: mproLabOverview,
      },
    ],
  },
  {
    id: 'help',
    title: 'Getting help',
    items: [
      {
        slug: 'getting-help',
        title: 'Asking a question',
        summary:
          'How to reach the community for support, and what it can and cannot do for you.',
        markdown: gettingHelp,
      },
    ],
  },
  {
    id: 'licences',
    title: 'NFT Licence',
    items: [
      {
        slug: 'nft-licence',
        title: 'Licences, distribution & transfers',
        summary:
          'What an iNODE licence entitles you to, how distribution follows it, and what a transfer carries with it.',
        markdown: licences,
      },
    ],
  },
  {
    id: 'mpro',
    title: 'MPRO Token',
    items: [
      {
        slug: 'mpro-token',
        title: 'Token & distribution',
        summary:
          'The token, its 500M cap, the transfer burn, and the 0.003 ETH platform fee charged per mint at claim.',
        markdown: mproToken,
      },
    ],
  },
  {
    id: 'dao',
    title: 'DAO',
    items: [
      {
        slug: 'dao',
        title: 'Governance',
        summary:
          'Who may propose, how votes are weighted, and the thresholds a proposal must clear to pass.',
        markdown: dao,
      },
    ],
  },
  {
    id: 'community',
    title: 'Community',
    items: [
      {
        slug: 'community',
        title: 'Profile, clans & standings',
        summary:
          'Profiles, clans and standings — including what stays private until you opt in.',
        markdown: community,
      },
    ],
  },
  {
    id: 'marketplace',
    title: 'Marketplace',
    items: [
      {
        slug: 'marketplace',
        title: 'Licence marketplace',
        summary:
          'Buying and selling licences: listings, buy offers, and the marketplace fee taken from a sale.',
        markdown: marketplace,
      },
    ],
  },
  {
    id: 'fee-vault',
    title: 'Fee Vault',
    items: [
      {
        slug: 'fee-vault',
        title: 'Marketplace fees, shared per licence',
        summary:
          'Where the 10% marketplace fee goes, how a licence earns its equal share, and how to claim it.',
        markdown: feeVault,
      },
    ],
  },
  {
    id: 'swap',
    title: 'Swap',
    items: [
      {
        slug: 'swap',
        title: 'Trading tokens on Base',
        summary:
          'Trading tokens on Base — and the MPRO Lab swap router every swap passes through, which takes a 0.25% platform fee from the token you sell.',
        markdown: swap,
      },
    ],
  },
  /*
   * Its own section, not a page under Swap.
   *
   * It was filed there because swapping one is how you act on it — and it then
   * sat two levels down and below the fold, so somebody looking for "what are
   * these stock tokens" did not find it. A tokenized share is an instrument
   * with an issuer, a prospectus and a set of powers over your wallet; that is
   * a subject, not a footnote to a trading screen.
   */
  {
    id: 'stocks',
    title: 'Tokenized Stocks',
    items: [
      {
        slug: 'tokenized-stocks',
        title: 'What they are, and who issues them',
        summary:
          'Tokenized US equities on Base: what a B20 share token is, who issues it and on what terms, what the issuer can do to your wallet, and how to buy one through MPRO Lab.',
        markdown: tokenizedStocks,
      },
    ],
  },
  {
    id: 'storage',
    title: 'Storage',
    items: [
      {
        slug: 'storage',
        title: 'NODE storage',
        summary:
          'The private storage area attached to each licence, and what happens to it on transfer.',
        markdown: storage,
      },
    ],
  },
  /*
   * Terminal is a section, not a page.
   *
   * It has eight modules a holder has to understand before they trust it with
   * a key, and one page describing all of them is a page nobody finishes. The
   * order is the order somebody meets them: what it is, how it opens, where
   * the money lives, then the things you do with it.
   */
  {
    id: 'terminal',
    title: 'Terminal',
    items: [
      {
        slug: 'terminal',
        title: 'What Terminal is',
        summary:
          'What the Terminal desktop application is, what it needs to run, and what it refuses to do.',
        markdown: terminal,
      },
      {
        slug: 'terminal-licence',
        title: 'Your licence is the key',
        summary:
          'Signing in, binding one iNODE licence to one Terminal, and what happens when that licence is sold.',
        markdown: terminalLicence,
      },
      {
        slug: 'terminal-wallet',
        title: 'Wallets',
        summary:
          'Creating and importing accounts, derived wallets, sending and receiving, and the single password everything is sealed with.',
        markdown: terminalWallet,
      },
      {
        slug: 'terminal-assistant',
        title: 'The terminal & the assistant',
        summary:
          'The two surfaces you talk to a model through, and why only one of them can act.',
        markdown: terminalAssistant,
      },
      {
        slug: 'terminal-models',
        title: 'LLM models',
        summary:
          'Choosing a local model, what your hardware can run, and how downloads are verified.',
        markdown: terminalModels,
      },
      {
        slug: 'terminal-skills',
        title: 'Skills',
        summary:
          'The slash commands — what they guarantee before they touch anything.',
        markdown: terminalSkills,
      },
      {
        slug: 'terminal-channels',
        title: 'Channels',
        summary:
          'Folders, channels and invitations, the clan channel nobody creates, and where messages are stored.',
        markdown: terminalChannels,
      },
      {
        slug: 'terminal-marketplace',
        title: 'Marketplace',
        summary:
          'Buying and publishing bots, the author fee taken by the router, and the Fee Vault the platform fee goes to.',
        markdown: terminalMarketplace,
      },
    ],
  },
]

const flatItems: DocumentationItem[] = documentationRegistry.flatMap((g) => g.items)

export function getDocumentationItemBySlug(slug: string | undefined): DocumentationItem | undefined {
  if (!slug) return undefined
  return flatItems.find((item) => item.slug === slug)
}

export function getAllDocumentationSlugs(): string[] {
  return flatItems.map((i) => i.slug)
}
